import { useId, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { VideoRenderer } from '@course-authoring/ui-blocks';
import type { CenterstageVideo } from '../../api/centerstageApi';
import { useVideoDetail } from '../../api/centerstageQueries';
import { AdminStatusMessage } from '../../admin-ui';
import { ModalShell } from '../ModalShell';
import { CENTERSTAGE_COPY as COPY, formatRuntime } from './centerstageCopy';
import type { CenterstageStyles } from './centerstageStyles';
import { useKeepFocusOnPlayer } from './useKeepFocusOnPlayer';

/** What the dialog shows before the video's detail loads: a library card, or a chat scenario row. */
export type CenterstageVideoSummary = Pick<CenterstageVideo, 'id' | 'title'>
  & Partial<Pick<CenterstageVideo, 'url' | 'description' | 'duration' | 'thumbnailUrl' | 'posterUrl'>>;

export interface CenterstageVideoDialogProps {
  styles: CenterstageStyles;
  /** The video to show; null keeps the dialog closed. */
  video: CenterstageVideoSummary | null;
  onClose: () => void;
  /** More details under the description (the Centerstage page's Type, Setting and concept links). */
  children?: ReactNode;
  /** The action row's contents; no row renders without it. */
  footer?: ReactNode;
}

/**
 * One Centerstage video: the player with a floating close, the title, ID and
 * run time, and the description, loaded from the video's detail. The
 * Centerstage page and the Author with AI picker each add their own actions.
 */
export function CenterstageVideoDialog({ styles, video, onClose, children, footer }: CenterstageVideoDialogProps) {
  const titleId = useId();
  const detailQuery = useVideoDetail(video?.id ?? null);
  const { playerRef, keepFocus } = useKeepFocusOnPlayer();

  const detail = detailQuery.data ?? null;
  const missing = detailQuery.isSuccess && detail === null;
  const title = detail?.title ?? video?.title ?? '';
  const description = detail?.description ?? video?.description;
  const runtime = formatRuntime(detail?.duration ?? video?.duration);

  return (
    <ModalShell
      isOpen={video !== null}
      onClose={onClose}
      ariaLabelledBy={titleId}
      presentation="admin"
      width="560px"
      centerOnContent
    >
      {video ? (
        <>
          <div style={styles.dialogScroll}>
            <div ref={playerRef} tabIndex={-1} style={styles.player}>
              <button type="button" aria-label={COPY.closeDialog} onClick={onClose} style={styles.floatingClose}>
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '16px' }} />
              </button>
              {/* Full width, so the player and its poster fill the dialog's 16:9 area for every video. */}
              <VideoRenderer
                constrainWidth={false}
                url={detail?.url ?? video.url}
                posterUrl={detail?.posterUrl ?? video.posterUrl ?? video.thumbnailUrl}
                onPlaybackStarted={keepFocus}
                onPlaybackCompleted={keepFocus}
              />
            </div>
            <div style={styles.dialogBody}>
              <h2 id={titleId} style={styles.dialogTitle}>{title}</h2>
              <p style={styles.dialogMeta}>
                {[COPY.videoId(video.id), runtime].filter(Boolean).join(' · ')}
              </p>
              {description ? <p style={styles.dialogDescription}>{description}</p> : null}
              {detailQuery.isPending ? <p style={styles.dialogMeta}>{COPY.detailLoading}</p> : null}
              {detailQuery.isError ? <AdminStatusMessage tone="error">{COPY.detailError}</AdminStatusMessage> : null}
              {missing ? <AdminStatusMessage tone="warning">{COPY.detailMissing}</AdminStatusMessage> : null}
              {children}
            </div>
          </div>
          {footer ? <div style={styles.dialogFooter}>{footer}</div> : null}
        </>
      ) : null}
    </ModalShell>
  );
}
